import { FaCalculator, FaPlus, FaSearch, FaSpinner, FaTrash } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api7Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const waterEntries = state.waterEntries || [];
    const recyclingEntries = state.recyclingEntries || [];
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const waterSources = ['Municipal grid', 'On-site well', 'Surface water', 'Third-party supplier', 'Other'];
    const dischargeDestinations = ['Sewer to municipal treatment', 'On-site treatment', 'Direct to surface', 'Sent offsite', 'Evaporation'];
    const processUses = ['Process wash', 'Cooling tower', 'Dust suppression', 'Boiler feed', 'Sanitary', 'Irrigation', 'Other'];

    const defaultWaterEmissionFactors = {
        'Municipal grid': { activity_id: "water_supply-type_na", source: "BEIS", region: "GB", year: 2021, unit: "kg/m3", unit_type: "Volume", name: "Water supply", co2e_factor: 0.149 },
        'On-site well': { activity_id: "water_supply-type_na", source: "BEIS", region: "GB", year: 2021, unit: "kg/m3", unit_type: "Volume", name: "Water supply", co2e_factor: 0.149 },
        'Surface water': { activity_id: "water_supply-type_na", source: "BEIS", region: "GB", year: 2021, unit: "kg/m3", unit_type: "Volume", name: "Water supply", co2e_factor: 0.149 },
        'Third-party supplier': { activity_id: "water_supply-type_na", source: "BEIS", region: "GB", year: 2021, unit: "kg/m3", unit_type: "Volume", name: "Water supply", co2e_factor: 0.149 },
        'Other': { activity_id: "water_supply-type_na", source: "BEIS", region: "GB", year: 2021, unit: "kg/m3", unit_type: "Volume", name: "Water supply", co2e_factor: 0.149 }
    };

    const wastewaterCarbonIntensity = {
        'Sewer to municipal treatment': 0.285,
        'On-site treatment': 0.400,
        'Direct to surface': 0.050,
        'Sent offsite': 0.350,
        'Evaporation': 0.100
    };

    const waterSearchConfig = {
        'Municipal grid': { query: 'water supply municipal', preferred_unit_type: 'Volume' },
        'On-site well': { query: 'water supply groundwater', preferred_unit_type: 'Volume' },
        'Surface water': { query: 'water supply surface', preferred_unit_type: 'Volume' },
        'Third-party supplier': { query: 'water supply', preferred_unit_type: 'Volume' },
        'Other': { query: 'water supply', preferred_unit_type: 'Volume' }
    };

    const conversionRates = {
        'Municipal grid': 2.5, 'On-site well': 0.8, 'Surface water': 0.5, 'Third-party supplier': 3.0, 'Other': 2.0
    };

    const findBestWaterFactor = (results, config) => {
        if (!results || results.length === 0) return null;
        const priorityOrder = [
            factor => factor.unit_type === 'Volume' && (factor.name.toLowerCase().includes('water') || factor.category === 'Water Supply'),
            factor => factor.unit_type === config.preferred_unit_type,
            factor => factor.name.toLowerCase().includes('water') || factor.category === 'Water',
            () => true
        ];
        for (const priorityFn of priorityOrder) {
            const factor = results.find(priorityFn);
            if (factor) return factor;
        }
        return results[0];
    };

    const searchEmissionFactors = async (waterSource) => {
        try {
            const config = waterSearchConfig[waterSource];
            if (!config) return defaultWaterEmissionFactors[waterSource];

            const params = new URLSearchParams({
                query: config.query,
                data_version: '^3',
                region: 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10',
                category: 'water_supply'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return defaultWaterEmissionFactors[waterSource];
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const bestFactor = findBestWaterFactor(data.results, config);
                if (bestFactor) {
                    updateState({ availableFactors: { ...state.availableFactors, [waterSource]: bestFactor } });
                    return bestFactor;
                }
            }

            return defaultWaterEmissionFactors[waterSource];
        } catch (error) {
            console.error(`Error searching emission factors for ${waterSource}:`, error);
            return defaultWaterEmissionFactors[waterSource];
        }
    };

    const handleSearchFactors = async () => {
        if (waterEntries.length === 0) {
            toastr.error('Please add at least one water entry first');
            return;
        }

        const sourcesToSearch = [...new Set(waterEntries.map(entry => entry.waterSource).filter(Boolean))];
        if (sourcesToSearch.length === 0) {
            toastr.error('Please add entries with water sources first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Searching for water emission factors...');

        try {
            const searchPromises = sourcesToSearch.map(source => searchEmissionFactors(source));
            const results = await Promise.allSettled(searchPromises);

            let foundCount = 0;
            results.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value) {
                    const source = sourcesToSearch[index];
                    if (result.value !== defaultWaterEmissionFactors[source]) foundCount++;
                }
            });

            if (foundCount > 0) {
                toastr.success(`Found custom emission factors for ${foundCount} water source(s)!`);
            } else {
                toastr.info('Using default BEIS water factors.');
            }
        } catch (error) {
            toastr.error('Search failed. Using default BEIS water factors.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const calculateVolumeFromSpend = (entry, currentVolume) => {
        let volume = currentVolume;
        if ((!volume || isNaN(volume)) && entry.annualSpend) {
            if (entry.tariff) {
                volume = parseFloat(entry.annualSpend) / parseFloat(entry.tariff);
                toastr.info(`Calculated volume for ${entry.waterSource}: ${volume.toFixed(2)} m³ from spend $${entry.annualSpend} and tariff $${entry.tariff}/m³`);
            } else {
                const rate = conversionRates[entry.waterSource];
                if (rate) {
                    volume = parseFloat(entry.annualSpend) / rate;
                    toastr.info(`Calculated volume for ${entry.waterSource}: ${volume.toFixed(2)} m³ from spend $${entry.annualSpend}`);
                }
            }
        }
        return volume;
    };

    const calculateWastewaterEmissions = (entry) => {
        let wastewaterEmissions = 0;
        if (entry.volumeDischarged && entry.dischargeDestination) {
            const dischargeVolume = parseFloat(entry.volumeDischarged);
            if (!isNaN(dischargeVolume) && dischargeVolume > 0) {
                wastewaterEmissions = dischargeVolume * wastewaterCarbonIntensity[entry.dischargeDestination];
            }
        }
        return wastewaterEmissions;
    };

    const calculateWaterEmissions = async (entry) => {
        try {
            const emissionFactor = state.availableFactors?.[entry.waterSource] || defaultWaterEmissionFactors[entry.waterSource];
            let volume = parseFloat(entry.volumeWithdrawn) || 0;
            volume = calculateVolumeFromSpend(entry, volume);

            if (volume <= 0 || isNaN(volume)) {
                throw new Error(`Invalid volume for ${entry.waterSource}. Please provide either volume (m³) or annual spend.`);
            }

            const requestBody = {
                emission_factor: {
                    activity_id: emissionFactor.activity_id,
                    source: emissionFactor.source,
                    region: emissionFactor.region,
                    year: parseInt(emissionFactor.year),
                    data_version: "^3"
                },
                parameters: {
                    volume: volume,
                    volume_unit: "m3"
                }
            };

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Water API request failed with status ${response.status}: ${errorData}`);
            }

            const data = await response.json();
            let co2e = data.co2e;
            co2e += calculateWastewaterEmissions(entry);

            return {
                co2e,
                co2e_unit: 'kg',
                calculation_method: 'water_supply_api',
                activity_data: { volume: volume, volume_unit: 'm3' },
                emission_factor_used: emissionFactor
            };
        } catch (error) {
            console.error(`Error calculating emissions for ${entry.waterSource}:`, error);
            let volume = parseFloat(entry.volumeWithdrawn) || 0;
            volume = calculateVolumeFromSpend(entry, volume);
            if (volume <= 0 || isNaN(volume)) throw new Error(`Invalid volume for ${entry.waterSource}`);

            const emissionFactor = defaultWaterEmissionFactors[entry.waterSource];
            let co2e = volume * emissionFactor.co2e_factor;
            co2e += calculateWastewaterEmissions(entry);

            return {
                co2e,
                co2e_unit: 'kg',
                calculation_method: 'default_water_factor',
                activity_data: { volume: volume, volume_unit: 'm3' },
                emission_factor_used: emissionFactor
            };
        }
    };

    const validateWaterEntries = () => {
        const errors = [];
        waterEntries.forEach((entry, index) => {
            if (!entry.site) errors.push(`Entry ${index + 1}: Please enter a site name`);
            if (!entry.waterSource) errors.push(`Entry ${index + 1}: Please select a water source`);
            if (!entry.volumeWithdrawn && !entry.annualSpend) errors.push(`Entry ${index + 1}: Please provide either volume (m³) or annual spend`);
            if (entry.annualSpend && !entry.tariff && !conversionRates[entry.waterSource]) {
                errors.push(`Entry ${index + 1}: Please provide a tariff or ensure water source has default conversion rate`);
            }
        });
        return errors;
    };

    const handleCalculate = async () => {
        if (waterEntries.length === 0) {
            toastr.error('Please add at least one water entry');
            return;
        }

        const validationErrors = validateWaterEntries();
        if (validationErrors.length > 0) {
            validationErrors.forEach(error => toastr.error(error));
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating water-related emissions...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < waterEntries.length; i++) {
                const entry = waterEntries[i];

                try {
                    const apiResult = await calculateWaterEmissions(entry);
                    const resultKey = `water_${i}`;

                    results[resultKey] = {
                        type: 'water_supply',
                        site: entry.site,
                        waterSource: entry.waterSource,
                        volume: parseFloat(entry.volumeWithdrawn) || calculateVolumeFromSpend(entry, 0),
                        unit: 'm³',
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: apiResult.calculation_method,
                        activity_data: apiResult.activity_data,
                        emission_factor: apiResult.emission_factor_used
                    };

                    const methodText = apiResult.calculation_method === 'water_supply_api' ? 'ClimateIQ API' : 'Default BEIS factor';
                    toastr.success(`Calculated emissions for ${entry.waterSource} at ${entry.site}: ${apiResult.co2e.toFixed(2)} kg CO₂e (${methodText})`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`Calculation error for water entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for water entry ${i + 1}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO₂e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const handleAddWaterEntry = () => {
        const newEntries = [...waterEntries, {
            site: '', period: formData.year || new Date().getFullYear().toString(), waterSource: '',
            volumeWithdrawn: '', dischargeDestination: '', volumeDischarged: '',
            annualSpend: '', tariff: '', evidence: null
        }];
        updateState({ waterEntries: newEntries });
    };

    const handleAddRecyclingEntry = () => {
        const newEntries = [...recyclingEntries, {
            site: '', period: formData.year || new Date().getFullYear().toString(),
            volumeRecycled: '', processUse: '', evidence: null
        }];
        updateState({ recyclingEntries: newEntries });
    };

    const handleWaterEntryChange = (index, field, value) => {
        const updated = [...waterEntries];
        updated[index][field] = value;
        updateState({ waterEntries: updated });
    };

    const handleRecyclingEntryChange = (index, field, value) => {
        const updated = [...recyclingEntries];
        updated[index][field] = value;
        updateState({ recyclingEntries: updated });
    };

    const handleRemoveWaterEntry = (index) => {
        updateState({ waterEntries: waterEntries.filter((_, i) => i !== index) });
    };

    const handleRemoveRecyclingEntry = (index) => {
        updateState({ recyclingEntries: recyclingEntries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025', recyclesWater: 'yes' },
            waterEntries: [{
                site: 'Main Facility', period: '2025', waterSource: 'Municipal grid',
                volumeWithdrawn: '15000', dischargeDestination: 'Sewer to municipal treatment',
                volumeDischarged: '12000', annualSpend: '', tariff: '', evidence: null
            }],
            recyclingEntries: [{
                site: 'Main Facility', period: '2025', volumeRecycled: '3000',
                processUse: 'Cooling tower', evidence: null
            }]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    const totalRecycledVolume = recyclingEntries.reduce((sum, entry) => sum + (parseFloat(entry.volumeRecycled) || 0), 0);
    const totalWaterWithdrawn = waterEntries.reduce((sum, entry) => {
        const volume = parseFloat(entry.volumeWithdrawn) ||
            (entry.annualSpend && entry.tariff ? parseFloat(entry.annualSpend) / parseFloat(entry.tariff) :
                entry.annualSpend && conversionRates[entry.waterSource] ? parseFloat(entry.annualSpend) / conversionRates[entry.waterSource] : 0);
        return sum + (volume || 0);
    }, 0);

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    {(waterEntries.length > 0 || recyclingEntries.length > 0) && (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="text-sm font-semibold text-blue-800">Total Water Withdrawn</h3>
                                <p className="text-2xl font-bold text-blue-600">{totalWaterWithdrawn.toLocaleString()} m³</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h3 className="text-sm font-semibold text-green-800">Total Water Recycled</h3>
                                <p className="text-2xl font-bold text-green-600">{totalRecycledVolume.toLocaleString()} m³</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-sm font-semibold text-purple-800">Recycling Rate</h3>
                                <p className="text-2xl font-bold text-purple-600">
                                    {totalWaterWithdrawn > 0 ? ((totalRecycledVolume / totalWaterWithdrawn) * 100).toFixed(1) : 0}%
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">7.2 Water Withdrawn & Discharged</h3>

                        <div className="mb-6">
                            <button
                                onClick={handleSearchFactors}
                                disabled={state.isSearching}
                                className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {state.isSearching ? (
                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                ) : (
                                    <FaSearch className="w-4 h-4" />
                                )}
                                <span>{state.isSearching ? 'Searching...' : 'Search Water Factors'}</span>
                            </button>
                        </div>

                        {Object.keys(state.availableFactors || {}).length > 0 && (
                            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                                <h3 className="font-semibold text-green-800 mb-2">Available Water Emission Factors</h3>
                                <div className="space-y-2">
                                    {Object.entries(state.availableFactors).map(([source, factor]) => (
                                        <div key={source} className="text-sm text-green-700">
                                            <strong>{source}:</strong> {factor.name} ({factor.source}, {factor.region})
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h3 className="font-semibold text-blue-800 mb-2">Default Water Emission Factors (BEIS)</h3>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(defaultWaterEmissionFactors).map(([source, factor]) => (
                                    <div key={source} className="flex justify-between">
                                        <span className="text-blue-700">{source}:</span>
                                        <span className="font-medium">{factor.co2e_factor.toFixed(3)} kg CO₂e/m³</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handleAddWaterEntry}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6"
                        >
                            <FaPlus className="w-4 h-4" />
                            <span>Add Water Entry</span>
                        </button>

                        {waterEntries.map((entry, index) => {
                            const resultKey = `water_${index}`;
                            const result = state.calculationResults?.[resultKey];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Water Entry {index + 1}</h4>
                                        <button
                                            onClick={() => handleRemoveWaterEntry(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {result && (
                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                            <p className="text-sm text-green-800 font-medium">
                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </p>
                                            <p className="text-xs text-green-600">
                                                {entry.waterSource} at {entry.site}: {result.volume.toFixed(2)} {result.unit}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name/ID *</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., Main Facility"
                                                value={entry.site}
                                                onChange={(e) => handleWaterEntryChange(index, 'site', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024 (Jan-Dec)"
                                                value={entry.period}
                                                onChange={(e) => handleWaterEntryChange(index, 'period', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Water Source *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.waterSource}
                                                onChange={(e) => handleWaterEntryChange(index, 'waterSource', e.target.value)}
                                            >
                                                <option value="">Select water source</option>
                                                {waterSources.map(source => <option key={source} value={source}>{source}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Volume Withdrawn (m³) *</label>
                                            <input
                                                type="number"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Enter cubic meters"
                                                value={entry.volumeWithdrawn}
                                                onChange={(e) => handleWaterEntryChange(index, 'volumeWithdrawn', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Annual Spend (if m³ unknown)</label>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-gray-600">$</span>
                                                <input
                                                    type="number"
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Enter amount"
                                                    value={entry.annualSpend}
                                                    onChange={(e) => handleWaterEntryChange(index, 'annualSpend', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Water Tariff ($ per m³)</label>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-gray-600">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Price per m³"
                                                    value={entry.tariff}
                                                    onChange={(e) => handleWaterEntryChange(index, 'tariff', e.target.value)}
                                                />
                                                <span className="text-gray-600 text-sm">per m³</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Discharge Destination</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.dischargeDestination}
                                                onChange={(e) => handleWaterEntryChange(index, 'dischargeDestination', e.target.value)}
                                            >
                                                <option value="">Select destination</option>
                                                {dischargeDestinations.map(dest => <option key={dest} value={dest}>{dest}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Volume Discharged (m³)</label>
                                            <input
                                                type="number"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Enter cubic meters"
                                                value={entry.volumeDischarged}
                                                onChange={(e) => handleWaterEntryChange(index, 'volumeDischarged', e.target.value)}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={(e) => handleWaterEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            Q1: Does your company recycle its water use?
                        </label>
                        <div className="flex space-x-4 mb-6">
                            <button
                                onClick={() => updateState({ formData: { ...formData, recyclesWater: 'yes' } })}
                                className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.recyclesWater === 'yes'
                                        ? 'border-green-500 bg-green-500 text-white hover:bg-green-600'
                                        : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Yes
                            </button>
                            <button
                                onClick={() => updateState({ formData: { ...formData, recyclesWater: 'no' } })}
                                className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.recyclesWater === 'no'
                                        ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                        : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                No
                            </button>
                        </div>

                        {formData.recyclesWater === 'yes' && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">7.2 Water Recycling/Reuse</h3>
                                <button
                                    onClick={handleAddRecyclingEntry}
                                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 mb-6"
                                >
                                    <FaPlus className="w-4 h-4" />
                                    <span>Add Recycling Entry</span>
                                </button>

                                {recyclingEntries.map((entry, index) => (
                                    <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-semibold">Recycling Entry {index + 1}</h4>
                                            <button
                                                onClick={() => handleRemoveRecyclingEntry(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name/ID</label>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="e.g., Main Facility"
                                                    value={entry.site}
                                                    onChange={(e) => handleRecyclingEntryChange(index, 'site', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="e.g., 2025"
                                                    value={entry.period}
                                                    onChange={(e) => handleRecyclingEntryChange(index, 'period', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Volume Recycled (m³)</label>
                                                <input
                                                    type="number"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Enter cubic meters"
                                                    value={entry.volumeRecycled}
                                                    onChange={(e) => handleRecyclingEntryChange(index, 'volumeRecycled', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Process/Use</label>
                                                <select
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    value={entry.processUse}
                                                    onChange={(e) => handleRecyclingEntryChange(index, 'processUse', e.target.value)}
                                                >
                                                    <option value="">Select process/use</option>
                                                    {processUses.map(use => <option key={use} value={use}>{use}</option>)}
                                                </select>
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                                <input
                                                    type="file"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    onChange={(e) => handleRecyclingEntryChange(index, 'evidence', e.target.files[0])}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(state.calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div>
                                            <span className="font-medium text-gray-700">
                                                {result.site} - {result.waterSource}
                                            </span>
                                            <p className="text-sm text-gray-500">
                                                {result.volume.toFixed(2)} {result.unit}
                                            </p>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Water-Related Emissions:</span>
                                        <span className="text-blue-700">
                                            {Object.values(state.calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0).toFixed(2)} kg CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default Api7Form;